import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { dashboardSearchControllerSearch } from "@/api/__generated__/dashboard-search/dashboard-search";
import { useCurrentLeague, useCurrentOrganization, useCurrentPlatform } from "@/contexts";
import { usePartnerPermissions } from "@/hooks";
import { useT } from "@/i18n";
import { mapToGlobalSearchItem } from "@/mappers";
import { useAlbumPermissions } from "@/pages/album/components";
import { useNewsPermissions } from "@/pages/news/components";

import { GlobalSearchItem } from "./GlobalSearch.types";
import { getOptionLabel } from "./GlobalSearch.utils";
import { useGetPageSearchItems } from "./useGetPageSearchItems";
import { useGetSearchItemTarget } from "./useGetSearchItemTarget";
import { GlobalSearchMode } from "./useGlobalSearchMode";
import { useRecentSearchItems } from "./useRecentSearchItems";

const PAGE_LIMIT = 20;
const LEAGUE_SEARCH_ITEM_TYPES = new Set<GlobalSearchItem["type"]>([
  "page",
  "user",
  "team",
  "tournament",
  "transaction",
  "rankingRule",
  "rankingCategory",
  "tournamentCategory",
  "rankingPointBlock",
  "news",
  "partner",
  "album",
]);

type LoadOptionsContext = {
  cursor?: GlobalSearchCursor;
  signal?: AbortSignal;
};

/**
 * BE pagination is `?page=N` (1-based); we surface it through AsyncCommand's
 * cursor surface as the *next* page number. `null` from BE/our mapper means
 * "no more pages — stop loading."
 */
export type GlobalSearchCursor = number;

export type UseGlobalSearchReturn = {
  loadOptions: (
    search: string,
    context?: LoadOptionsContext
  ) => Promise<{ items: GlobalSearchItem[]; cursor: GlobalSearchCursor | null }>;
  getOptionLabel: (option: GlobalSearchItem) => string;
  getOptionKey: (option: GlobalSearchItem) => string;
  resetKey: string;
  recentItems: GlobalSearchItem[];
  addRecentItem: (item: GlobalSearchItem) => void;
  /** True when the popover is showing the empty-input recents list (i.e.
   * `loadOptions` was last called with an empty keyword). Surfaces switch
   * from `true` → `false` as soon as `loadOptions` resolves for a typed
   * query, so consumers can stop badging rows as "recent". */
  isShowingRecents: boolean;
};

export type UseGlobalSearchProps = {
  mode?: GlobalSearchMode;
  /**
   * Called after every BE-backed load (including pages-only fallbacks) with
   * the merged result set, latency, and page number. Used by the surface to
   * forward `global_search_query_executed` to analytics. Not called for the
   * empty-input recents path.
   */
  onQueryExecuted?: (props: {
    keyword: string;
    page: number;
    resultCount: number;
    items: GlobalSearchItem[];
    latencyMs: number;
  }) => void;
};

export const useGlobalSearch = ({
  mode = "usual",
  onQueryExecuted,
}: UseGlobalSearchProps = {}): UseGlobalSearchReturn => {
  const t = useT();
  const queryClient = useQueryClient();
  const currentPlatform = useCurrentPlatform();
  const currentOrganization = useCurrentOrganization();
  const currentLeague = useCurrentLeague();
  const getPageSearchItems = useGetPageSearchItems();
  const getSearchItemTarget = useGetSearchItemTarget(mode);
  const { canUpdateAlbum } = useAlbumPermissions();
  const { canUpdateNews } = useNewsPermissions();
  const { canManagePartners } = usePartnerPermissions();

  const canOpenSearchItem = useCallback(
    (item: GlobalSearchItem) => {
      // Explicit super-admin mode is globally authorized by the backend role
      // gate; active-scope hooks cannot describe permissions for results owned
      // by some other league. Usual mode must mirror the destination edit page.
      if (mode !== "superAdmin" && item.type === "album" && !canUpdateAlbum) return false;
      if (mode !== "superAdmin" && item.type === "news" && !canUpdateNews) return false;
      if (mode !== "superAdmin" && item.type === "partner" && !canManagePartners) return false;
      // Entity admission mirrors the same permission/module-filtered sidebar
      // model used for navigation. A result with no permitted destination must
      // not be shown as an inert row.
      return getSearchItemTarget(item) !== undefined;
    },
    [canManagePartners, canUpdateAlbum, canUpdateNews, getSearchItemTarget, mode]
  );

  // Recents are stored per tenant scope; this key stays stable across selections
  // so the localStorage list isn't re-bucketed every time it changes.
  const scopeKey = `${mode}:${currentPlatform?.id ?? ""}:${currentOrganization?.id ?? ""}:${currentLeague?.id ?? ""}`;
  const { recentItems: storedRecentItems, addRecentItem } = useRecentSearchItems(scopeKey);
  const recentItems = currentLeague
    ? storedRecentItems.filter(
        (item) => LEAGUE_SEARCH_ITEM_TYPES.has(item.type) && canOpenSearchItem(item)
      )
    : storedRecentItems.filter(canOpenSearchItem);

  // AsyncCommand snapshots `defaultOptions` (the recents) into its visible list
  // on mount and only re-syncs when `resetKey` changes — a plain focus opens the
  // popover without re-running `loadOptions("")`, so the list would otherwise
  // stay frozen at whatever was stored on mount. Folding a signature of the
  // current recents into the key makes a freshly-recorded pick show up the next
  // time the popover opens. The scope prefix still drives the tenant-switch
  // reset (drop cached pages / abort in-flight requests under the old scope).
  const resetKey = `${scopeKey}|${recentItems.map((item) => item.id).join(",")}`;

  // AsyncCommand → ComboBox always runs a client-side `contains` filter against
  // each option's textValue. The BE search is fuzzy / typo-tolerant, so hits
  // don't necessarily contain the literal typed string. We track the latest
  // input here and prefix it onto every option's label below, which guarantees
  // every BE-returned item passes the local filter. Only affects textValue —
  // the visible row still comes from `renderOption` / SearchItemView.
  const latestKeywordRef = useRef("");
  // Reactive companion to `latestKeywordRef` so the UI can re-render when
  // the popover flips between recents and BE results. We can't drive this
  // off the input directly because AsyncCommand's wrapper owns
  // `onInputChange` (it forwards the internal debounce trigger and our
  // override would clobber it — no API calls would fire).
  const [isShowingRecents, setIsShowingRecents] = useState(true);

  const loadOptions = useCallback(
    async (
      search: string,
      { cursor }: LoadOptionsContext = {}
    ): Promise<{ items: GlobalSearchItem[]; cursor: GlobalSearchCursor | null }> => {
      latestKeywordRef.current = search;
      const keyword = search.trim();
      // Empty input → surface a few recently picked items so the popover
      // isn't blank on focus. Cursor stays null because recents aren't
      // paginated.
      if (keyword === "") {
        setIsShowingRecents(true);
        return { items: recentItems, cursor: null };
      }
      setIsShowingRecents(false);

      const page = cursor ?? 1;
      const isFirstPage = page === 1;

      // Pages only render on the first page — they're local-only nav links and
      // shouldn't repeat when the user scrolls into subsequent BE pages.
      const pageSearchItems = isFirstPage ? getPageSearchItems(keyword) : [];

      // The endpoint keeps platformId as a required context identifier for
      // availability-shaped results and backwards compatibility. League scope
      // takes precedence over it in usual mode, while super-admin mode ignores
      // it for globally searchable entities.
      if (!currentPlatform?.id) {
        onQueryExecuted?.({
          keyword,
          page,
          resultCount: pageSearchItems.length,
          items: pageSearchItems,
          latencyMs: 0,
        });
        return { items: pageSearchItems, cursor: null };
      }

      const params = {
        keyword,
        mode,
        platformId: currentPlatform.id,
        ...(mode === "usual" && currentLeague?.id
          ? { leagueId: currentLeague.id }
          : mode === "usual"
            ? {
                ...(currentOrganization?.id ? { federationId: currentOrganization.id } : {}),
              }
            : {}),
        page,
        limit: PAGE_LIMIT,
      };

      const startedAt = performance.now();
      const response = await queryClient.fetchQuery({
        queryKey: ["/api/dashboard/v1/search", params],
        queryFn: ({ signal }) =>
          // The dashboard OpenAPI client has not been regenerated for `mode`
          // / `leagueId` yet. Runtime serialization already accepts the new
          // shape; remove this assertion after backend codegen lands.
          dashboardSearchControllerSearch(
            params as Parameters<typeof dashboardSearchControllerSearch>[0],
            undefined,
            signal
          ),
        staleTime: 30_000,
      });
      const latencyMs = performance.now() - startedAt;

      const remoteItems = response.data
        .map((raw) => mapToGlobalSearchItem(raw))
        .filter((item): item is GlobalSearchItem => item !== null)
        .filter(canOpenSearchItem)
        .filter((item) => {
          // Don't surface the current platform/organization as a switch target.
          if (item.type === "platform") return item.platform.id !== currentPlatform?.id;
          if (item.type === "organization") {
            return !currentOrganization || item.organization.id !== currentOrganization.id;
          }
          return true;
        });

      const hasMore = response.meta.page < response.meta.totalPages;
      const nextCursor: GlobalSearchCursor | null = hasMore ? response.meta.page + 1 : null;

      // Pages pinned on top of the first BE page; BE owns ordering of the rest
      // by similarity score and merges subsequent pages underneath.
      const items = [...pageSearchItems, ...remoteItems];
      onQueryExecuted?.({
        keyword,
        page,
        resultCount: items.length,
        items,
        latencyMs,
      });
      return { items, cursor: nextCursor };
    },
    [
      queryClient,
      mode,
      currentPlatform?.id,
      currentOrganization,
      currentLeague?.id,
      getPageSearchItems,
      recentItems,
      onQueryExecuted,
      canOpenSearchItem,
    ]
  );

  const getOptionLabelForFilter = useCallback(
    // Prefix the live keyword so the ComboBox's built-in `contains` filter
    // always matches. `getOptionLabel` is used for textValue (filter + a11y
    // name) — the visual label comes from `renderOption`.
    (option: GlobalSearchItem) => {
      const realLabel = getOptionLabel(option, t("general.text.untitled"));
      const keyword = latestKeywordRef.current;
      return keyword ? `${keyword} ${realLabel}` : realLabel;
    },
    [t]
  );

  const getOptionKey = useCallback((option: GlobalSearchItem) => option.id, []);

  return {
    loadOptions,
    getOptionLabel: getOptionLabelForFilter,
    getOptionKey,
    resetKey,
    recentItems,
    addRecentItem,
    isShowingRecents,
  };
};
